import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {FlexColumn, FlexRow, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {NovaInputIssue, NovaIssueKind} from '../shared/novaInputValidation';
import {NovaMarkdownWarning} from '../shared/novaMarkdownScan';
import {NOVA_MONOSPACE_FONT} from '../shared/novaStudioFonts';

const KIND_LABEL_ID: Record<NovaIssueKind, string> = {
	[NovaIssueKind.BrokenReference]: 'nova.inputIssues.brokenLabel',
	[NovaIssueKind.UnknownVariable]: 'nova.inputIssues.unknownLabel',
	[NovaIssueKind.Markdown]: 'nova.inputIssues.markdownLabel',
};

const WARNING_MESSAGE_ID: Record<NovaMarkdownWarning, string> = {
	[NovaMarkdownWarning.UnclosedCodeFence]: 'nova.inputIssues.unclosedCodeFence',
	[NovaMarkdownWarning.UnclosedInlineCode]: 'nova.inputIssues.unclosedInlineCode',
	[NovaMarkdownWarning.UnclosedLinkTarget]: 'nova.inputIssues.unclosedLinkTarget',
};

// The token is shown in the same fixed-width font as the editor it came from, so the author can
// match it against what they typed character for character.
const Token = styled.span`
	font-family: ${NOVA_MONOSPACE_FONT};
`;

interface Props {
	issues: NovaInputIssue[];
	dataCy: string;
}

// Advisory feedback about one authored input: bad variable references and broken Markdown, each
// named in plain language. Nothing here is an error — none of it blocks saving, and none of it
// changes the author's text. A reference that no longer resolves is reported as broken rather than
// left silently valid, but it is still the author's call what to do about it.
//
// Renders nothing when there is nothing to say, so a clean input costs no vertical space.
export function NovaInputIssues({issues, dataCy}: Props): JSX.Element | null {
	const {formatMessage} = useIntl();

	if (issues.length === 0) {
		return null;
	}

	const describe = (issue: NovaInputIssue): string => {
		switch (issue.kind) {
			case NovaIssueKind.Markdown:
				return issue.warning ? formatMessage({id: WARNING_MESSAGE_ID[issue.warning]}) : '';
			case NovaIssueKind.BrokenReference:
				return formatMessage({id: 'nova.inputIssues.brokenReason'});
			case NovaIssueKind.UnknownVariable:
				return formatMessage({id: 'nova.inputIssues.unknownReason'});
			default: {
				// A new issue kind must be given its own reason here — assigning to `never` makes
				// that a compile error rather than a row that silently reads as not-permitted.
				const unhandled: never = issue.kind;
				return unhandled;
			}
		}
	};

	return (
		<div data-cy={dataCy} data-testid={dataCy}>
			<FlexColumn gap="xs">
				{issues.map(issue => (
					<FlexRow key={`${issue.kind}-${issue.token ?? issue.warning ?? ''}`} gap="s" alignItems="baseline">
						<Pill variant={PillVariant.NEUTRAL}>{formatMessage({id: KIND_LABEL_ID[issue.kind]})}</Pill>
						{issue.token ? <Token>{issue.token}</Token> : null}
						<Text color="auxiliary">{describe(issue)}</Text>
					</FlexRow>
				))}
			</FlexColumn>
		</div>
	);
}

export default NovaInputIssues;
